import React from 'react';

import { Input } from '@dashboard/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import { countries } from '@dashboard/services/countries/countries';
import { getAddressData } from '@dashboard/services/getAddressData';

const ADDRESS_FIELDS = ['line1', 'line2', 'city', 'region', 'postalCode', 'country'];
const SHIPPING_FIELDS = ['phone', ...ADDRESS_FIELDS];

const FIELD_PLACEHOLDERS = {
  line1: 'Address line 1',
  line2: 'Address line 2',
  city: 'City',
  region: 'State / Province / Region',
  postalCode: 'Postal code',
  phone: 'Phone',
  country: 'Country',
};

function getFieldsForMode(mode) {
  return mode === 'shipping' ? SHIPPING_FIELDS : ADDRESS_FIELDS;
}

function getModesToRender(pageField, value) {
  // Prefer the explicit page-field setting when available.
  // `collect` is `'billing' | 'billing_and_shipping'`. Default to billing-only
  // (matches legacy behaviour for fields without explicit collect).
  const collect = pageField?.collect;

  if (collect === 'billing_and_shipping') return ['shipping', 'billing'];
  if (collect === 'billing') return ['billing'];

  // Fall back to inferring from data so we still render the right cards if
  // the page-field metadata is missing for some reason.
  const modes = [];
  if (value?.shipping) modes.push('shipping');
  if (value?.billing) modes.push('billing');
  if (modes.length === 0) modes.push('billing');
  return modes;
}

function pruneEmpty(sub, fields) {
  if (!sub) return undefined;
  const out = {};
  let hasAny = false;
  for (const key of fields) {
    const raw = sub[key];
    if (typeof raw === 'string' && raw.length > 0) {
      out[key] = raw;
      hasAny = true;
    }
  }
  return hasAny ? out : undefined;
}

function buildStructuredValue(modes, working) {
  const next = {};
  for (const mode of modes) {
    const pruned = pruneEmpty(working[mode], getFieldsForMode(mode));
    if (pruned) next[mode] = pruned;
  }
  return next;
}

function MODE_HEADING(mode) {
  return mode === 'shipping' ? 'Shipping address' : 'Billing address';
}

function CountrySelect({ id, value, onChange }) {
  // Radix Select treats `undefined` as uncontrolled; pass `undefined` for the
  // empty state so the trigger renders the placeholder instead of a blank slot.
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger id={id} aria-label="Country" className="w-full">
        <SelectValue placeholder="Select country" />
      </SelectTrigger>
      <SelectContent>
        {countries.map(c => (
          <SelectItem key={c.alpha2} value={c.alpha2}>
            {c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function AddressCard({ mode, value, idPrefix, onChange }) {
  const v = value || {};
  const fields = getFieldsForMode(mode);

  const setSubField = (key, raw) => {
    onChange({ ...v, [key]: raw });
  };

  return (
    <div className="border border-gray-200 dark:border-border rounded-md p-4 space-y-1">
      <div className="text-sm font-medium text-gray-900 dark:text-foreground mb-2">
        {MODE_HEADING(mode)}
      </div>
      {fields.map(key => {
        const id = `${idPrefix}-${mode}-${key}`;
        const placeholder = FIELD_PLACEHOLDERS[key];
        if (key === 'country') {
          return (
            <CountrySelect
              key={key}
              id={id}
              value={v.country}
              onChange={next => setSubField('country', next)}
            />
          );
        }

        return (
          <Input
            key={key}
            id={id}
            name={id}
            type={key === 'phone' ? 'tel' : 'text'}
            value={v[key] ?? ''}
            onChange={e => setSubField(key, e.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
          />
        );
      })}
    </div>
  );
}

export default function AddressFieldEdit({ field, values, setFieldValue }) {
  const fieldName = field.name;
  const initial = field._value && typeof field._value === 'object' ? field._value : {};
  const currentRaw = values?.[fieldName];

  // The Formik value can be either:
  //  - an object (post our edits) — { billing?, shipping? } shape
  //  - a string (legacy) — fall back to the initial structured value
  const current = currentRaw && typeof currentRaw === 'object' ? currentRaw : initial;

  const modes = getModesToRender(field.pageField, getAddressData(field) ?? initial);

  const working = {};
  for (const mode of modes) {
    working[mode] = current?.[mode] ?? initial?.[mode] ?? {};
  }

  const handleSubAddressChange = (mode, nextSub) => {
    const nextWorking = { ...working, [mode]: nextSub };
    const structured = buildStructuredValue(modes, nextWorking);
    setFieldValue(fieldName, structured);
  };

  return (
    <div className="space-y-3">
      {modes.map(mode => (
        <AddressCard
          key={mode}
          mode={mode}
          idPrefix={fieldName}
          value={working[mode]}
          onChange={next => handleSubAddressChange(mode, next)}
        />
      ))}
    </div>
  );
}
